/**
 * Preflight the microphone permission for host/guest users the first time
 * they land on their messages inbox.
 *
 * WHY (2026-02):
 *   • Web / PWA: getUserMedia({audio:true}) already surfaces the browser
 *     mic prompt, but calling it lazily from inside the phone-button
 *     handler consumes the user gesture window (call-initiate POST +
 *     Twilio ICE fetch) and can silently fail with "Unable to start the
 *     call". Priming from the messages page fixes that.
 *   • Capacitor Android (remote-URL config): the WebView's
 *     BridgeWebChromeClient silently rejects getUserMedia when the app
 *     hasn't been granted RECORD_AUDIO at the OS layer, and NO OS prompt
 *     surfaces. We solve this by calling our local `MicPermission` native
 *     plugin (`ActivityCompat.requestPermissions`), same pattern used for
 *     the camera scanner.
 *
 * Anti-spam:
 *   • We store "asked" in localStorage under `allure:mic-permission-asked`
 *     the first time the OS prompt fires. If the user declines, we NEVER
 *     re-prompt on subsequent visits — they can re-enable from OS
 *     Settings. Mirrors how POST_NOTIFICATIONS is handled by the system.
 *
 * Scope:
 *   • Only 'guest' and 'host' roles ever preflight. Venues / venue members
 *     / staff / admins are silently skipped (they don't own a phone
 *     button, so we never want to spam a mic prompt on them).
 */
const CALL_ROLES = new Set(['guest', 'host']);
const ASKED_STORAGE_KEY = 'allure:mic-permission-asked';

function getMicPermissionPlugin() {
  try {
    return window?.Capacitor?.Plugins?.MicPermission || null;
  } catch (_) {
    return null;
  }
}

function isNativeCapacitor() {
  try {
    const cap = window?.Capacitor;
    return typeof cap?.isNativePlatform === 'function' && cap.isNativePlatform();
  } catch (_) {
    return false;
  }
}

function markAsked() {
  try { window.localStorage.setItem(ASKED_STORAGE_KEY, '1'); } catch (_) { /* private-mode etc. */ }
}

function alreadyAsked() {
  try { return window.localStorage.getItem(ASKED_STORAGE_KEY) === '1'; } catch (_) { return false; }
}

async function primeNative(plugin) {
  try {
    const status = await plugin.check();
    if (status?.microphone === 'granted') return;
    // If we've asked once already (even if the user declined), never
    // fire the OS dialog again — they must grant via system settings.
    if (alreadyAsked()) return;
    markAsked();
    await plugin.request();
  } catch (_) {
    // Plugin unavailable on this build — swallow.
  }
}

async function primeWeb() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
  try {
    if (navigator.permissions?.query) {
      try {
        const status = await navigator.permissions.query({ name: 'microphone' });
        if (status.state === 'granted' || status.state === 'denied') return;
      } catch (_) { /* some browsers don't support 'microphone' key */ }
    }
    if (alreadyAsked()) return;
    markAsked();
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Release immediately — we only wanted to fire the prompt.
    stream.getTracks().forEach((track) => track.stop());
  } catch (_) {
    // User denied or the API is unavailable — safe to ignore.
  }
}

export async function primeCallPermissions(role) {
  if (!CALL_ROLES.has(role)) return;
  if (isNativeCapacitor()) {
    const plugin = getMicPermissionPlugin();
    if (plugin) {
      await primeNative(plugin);
      return;
    }
  }
  await primeWeb();
}
